import React, { useState, FormEvent, useEffect } from "react";
import clsx from "clsx";
import { useStripe, useElements, IbanElement, CardElement } from "@stripe/react-stripe-js";
import { PiBankLight, PiBuildingsLight, PiCreditCardLight, PiGlobeLight } from "react-icons/pi";
import Input from "../../shared/TailwindComponents/Input";
import FieldShell, { fieldSurface } from "../../shared/TailwindComponents/FieldShell";
import SettingsSection from "../framework/SettingsSection";
import { useAppDispatch } from "../../../store";
import { createConnectedAccount } from "../../../store/actions/StripeActions";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getPreferences } from "../../../store/actions/DataAction";

type PaymentMethod = "card" | "iban" | "account";

/**
 * The organization's payout details.
 *
 * A settings section like any other: the method switcher is the app's tab
 * treatment, each Stripe element sits in a `FieldShell` over the standard field
 * surface, and Save is the section's own footer button - enabled only once the
 * chosen method has enough detail to tokenize, so the admin is not invited to
 * submit an empty card.
 */
const BankForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("card");
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const preferences = useSelector((state: any) => state.lists.preferences);

  const [selectedCurrency, setSelectedCurrency] = useState({ code: "USD", name: "" });
  const [accountDetails, setAccountDetails] = useState({
    routingNumber: "",
    accountNumber: "",
    accountHolderName: userInfo.FirstName + " " + userInfo.LastName,
  });
  // Stripe's own completeness signal for the hosted element in play.
  const [elementComplete, setElementComplete] = useState(false);

  useEffect(() => {
    dispatch(getPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (preferences) {
      setSelectedCurrency({ code: preferences.Currency, name: preferences.CurrencyName });
    }
  }, [preferences]);

  const selectMethod = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setElementComplete(false);
  };

  const ready =
    paymentMethod === "account"
      ? !!accountDetails.routingNumber.trim() && !!accountDetails.accountNumber.trim()
      : elementComplete;

  const submit = async () => {
    if (!stripe) return;
    setLoading(true);

    let token;
    let error;

    if (paymentMethod === "card") {
      const cardNumberElement = elements?.getElement(CardElement);
      ({ token, error } = await stripe.createToken(cardNumberElement!));
    } else if (paymentMethod === "iban") {
      const ibanElement = elements?.getElement(IbanElement);
      ({ token, error } = await stripe.createToken(ibanElement!, {
        currency: 'eur',
        account_holder_name: accountDetails.accountHolderName,
        account_holder_type: "individual",
      }));
    } else if (paymentMethod === "account") {
      ({ token, error } = await stripe.createToken("bank_account", {
        country: "NO",
        currency: selectedCurrency.code,
        routing_number: accountDetails.routingNumber,
        account_number: accountDetails.accountNumber,
        account_holder_name: accountDetails.accountHolderName,
        account_holder_type: "individual",
      }));
    }

    if (error) {
      setLoading(false);
      return;
    }

    await dispatch(createConnectedAccount(token?.id));
    setLoading(false);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  return (
    <form onSubmit={handleSubmit} data-testid="org-payment-method-section">
      <SettingsSection
        title={t("Payment method")}
        description={t("How your organization is paid out.")}
        icon={<PiBankLight />}
        saveMode="explicit"
        dirty={ready}
        isSaving={loading}
        onSave={submit}
        saveLabel={t("Save payment method")}
      >
        {/* The method switcher is a tab bar, so it wears the app's tab
            treatment (.glass-bar + .nav-pill) rather than its own colours. */}
        <div className="glass-bar flex gap-1 rounded-lg border border-hairline p-1" role="tablist">
          {[
            { type: "card", icon: <PiCreditCardLight size={18} />, label: "Card" },
            { type: "iban", icon: <PiGlobeLight size={18} />, label: "IBAN" },
            { type: "account", icon: <PiBuildingsLight size={18} />, label: "Bank Account" },
          ].map(({ type, icon, label }) => (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={paymentMethod === type}
              onClick={() => selectMethod(type as PaymentMethod)}
              // The field labels below are all `text-sm font-medium`. If the
              // tabs sit at that same weight the row reads as a peer of the
              // labels rather than as the control that switches them, so an
              // unselected tab drops to `font-normal` and only the selected
              // one carries weight.
              className={clsx(
                "nav-pill flex-1 flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm",
                paymentMethod === type
                  ? "nav-pill-active font-medium text-fg-strong"
                  : "font-normal text-fg-muted hover:text-fg-body"
              )}
            >
              {icon} {t(label)}
            </button>
          ))}
        </div>

        {paymentMethod === "card" && (
          <FieldShell
            label={t("Card Number")}
            helpText={t("Stored by Stripe — PONS never sees the number.")}
            translate={false}
          >
            <div className={clsx(fieldSurface({ height: false }), "px-3 py-2.5")}>
              <CardElement onChange={(e) => setElementComplete(e.complete)} />
            </div>
          </FieldShell>
        )}

        {paymentMethod === "iban" && (
          <FieldShell
            label={t("IBAN")}
            helpText={t("SEPA countries only.")}
            translate={false}
          >
            <div className={clsx(fieldSurface({ height: false }), "px-3 py-2.5")}>
              <IbanElement
                options={{ supportedCountries: ["SEPA"] }}
                onChange={(e) => setElementComplete(e.complete)}
              />
            </div>
          </FieldShell>
        )}

        {paymentMethod === "account" && (
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              type="text"
              label={t("Routing Number")}
              value={accountDetails.routingNumber}
              onChange={(e) => setAccountDetails({ ...accountDetails, routingNumber: e.target.value })}
            />
            <Input
              type="text"
              label={t("Account Number")}
              value={accountDetails.accountNumber}
              onChange={(e) => setAccountDetails({ ...accountDetails, accountNumber: e.target.value })}
            />
            <Input
              type="text"
              label={t("Account Holder")}
              description={t("The name the receiving account is registered under.")}
              value={accountDetails.accountHolderName}
              onChange={(e) =>
                setAccountDetails({ ...accountDetails, accountHolderName: e.target.value })
              }
            />
          </div>
        )}
      </SettingsSection>
    </form>
  );
};

export default BankForm;
